import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { predefinedLinesAPI } from '../services/api';

function PredefinedLinesPage() {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    line_type: 'service',
    pu_ht: '',
    reference: '',
  });

  // Sorting state
  const [orderBy, setOrderBy] = useState('description');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    loadLines();
  }, []);

  const loadLines = async () => {
    try {
      setLoading(true);
      const response = await predefinedLinesAPI.getAll();
      // Handle paginated response
      const linesData = response.data.results || response.data;
      setLines(linesData);
      setError(null);
    } catch (err) {
      console.error('Error loading predefined lines:', err);
      setError('Failed to load predefined lines. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLine = () => {
    setEditingLine(null);
    setFormData({
      description: '',
      line_type: 'service',
      pu_ht: '',
      reference: '',
    });
    setEditDialogOpen(true);
  };

  const handleEditLine = (line) => {
    setEditingLine(line);
    setFormData({
      description: line.description,
      line_type: line.line_type,
      pu_ht: line.pu_ht,
      reference: line.reference || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteLine = async (line) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${line.description}" ?`)) {
      return;
    }

    try {
      await predefinedLinesAPI.delete(line.id);
      setLines(prev => (Array.isArray(prev) ? prev : []).filter(l => l.id !== line.id));
    } catch (err) {
      console.error('Error deleting predefined line:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveLine = async () => {
    try {
      const data = {
        ...formData,
        pu_ht: parseFloat(formData.pu_ht),
      };

      if (editingLine) {
        const response = await predefinedLinesAPI.update(editingLine.id, data);
        setLines(prev => (Array.isArray(prev) ? prev : []).map(l => l.id === editingLine.id ? response.data : l));
      } else {
        const response = await predefinedLinesAPI.create(data);
        setLines(prev => [...(Array.isArray(prev) ? prev : []), response.data]);
      }

      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error saving predefined line:', err);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'installation':
        return 'primary';
      case 'subscription':
        return 'success';
      case 'service':
        return 'info';
      case 'maintenance':
        return 'warning';
      case 'equipment':
        return 'secondary';
      case 'product':
        return 'default';
      case 'discount':
        return 'error';
      case 'note':
        return 'info';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'installation':
        return 'Installation';
      case 'subscription':
        return 'Abonnement';
      case 'service':
        return 'Service';
      case 'maintenance':
        return 'Maintenance';
      case 'equipment':
        return 'Équipement';
      case 'product':
        return 'Produit';
      case 'discount':
        return 'Remise';
      case 'note':
        return 'Note';
      case 'other':
        return 'Autre';
      default:
        return type;
    }
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort comparator
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (orderBy === 'pu_ht') {
      aVal = parseFloat(aVal || 0);
      bVal = parseFloat(bVal || 0);
    }

    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const filteredLines = (Array.isArray(lines) ? lines : [])
    .filter(line => {
      const matchesSearch =
        line.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (line.reference && line.reference.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType = typeFilter === 'all' || line.line_type === typeFilter;

      return matchesSearch && matchesType;
    })
    .sort(getComparator(order, orderBy));

  const paginatedLines = filteredLines.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Lignes Prédéfinies
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadLines}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateLine}
          >
            Nouvelle Ligne
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par description ou référence..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="installation">Installation</MenuItem>
            <MenuItem value="subscription">Abonnement</MenuItem>
            <MenuItem value="service">Service</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="equipment">Équipement</MenuItem>
            <MenuItem value="product">Produit</MenuItem>
            <MenuItem value="discount">Remise</MenuItem>
            <MenuItem value="note">Note</MenuItem>
            <MenuItem value="other">Autre</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>
                <TableSortLabel
                  active={orderBy === 'description'}
                  direction={orderBy === 'description' ? order : 'asc'}
                  onClick={() => handleRequestSort('description')}
                >
                  <strong>Description</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'line_type'}
                  direction={orderBy === 'line_type' ? order : 'asc'}
                  onClick={() => handleRequestSort('line_type')}
                >
                  <strong>Type</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'pu_ht'}
                  direction={orderBy === 'pu_ht' ? order : 'asc'}
                  onClick={() => handleRequestSort('pu_ht')}
                >
                  <strong>Prix U. HT</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}><strong>Référence</strong></TableCell>
              <TableCell sx={{ minWidth: 120 }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Aucune ligne prédéfinie trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedLines.map((line) => (
                <TableRow key={line.id} hover>
                  <TableCell>{line.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(line.line_type)}
                      color={getTypeColor(line.line_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {parseFloat(line.pu_ht).toLocaleString('fr-FR')} DZD
                  </TableCell>
                  <TableCell>{line.reference || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleEditLine(line)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLine(line)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredLines.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLine ? 'Modifier la ligne prédéfinie' : 'Nouvelle ligne prédéfinie'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type de ligne</InputLabel>
                <Select
                  value={formData.line_type}
                  label="Type de ligne"
                  onChange={(e) => setFormData({ ...formData, line_type: e.target.value })}
                >
                  <MenuItem value="installation">Installation</MenuItem>
                  <MenuItem value="subscription">Abonnement</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="equipment">Équipement</MenuItem>
                  <MenuItem value="product">Produit</MenuItem>
                  <MenuItem value="discount">Remise</MenuItem>
                  <MenuItem value="note">Note</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prix unitaire HT"
                type="number"
                value={formData.pu_ht}
                onChange={(e) => setFormData({ ...formData, pu_ht: e.target.value })}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">DZD</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Référence (optionnel)"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveLine}
            variant="contained"
            disabled={!formData.description || !formData.pu_ht}
          >
            {editingLine ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PredefinedLinesPage;
